import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { idpConfigurationApi } from '../../../services/api/idpConfigurationApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ItemTitle, ItemDetailsGrid, OrderBadge, ActionButtons, ItalicText, DetailRow, Label } from '../styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ConfirmationModal from '../../common/ConfirmationModal';
import { useAlert } from '../../../contexts/AlertContext';
import { formatDateOnly } from '../../../utils/dateOnly';

const ResourceTemplates = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    category: '',
    exampleName: '',
    exampleDetails: '',
    order: 0,
    publishedAt: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [publishModal, setPublishModal] = useState({ open: false, item: null });
  const [unpublishModal, setUnpublishModal] = useState({ open: false, item: null });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await idpConfigurationApi.getAllResourceTemplates();
      setItems(data);
    } catch (err) {
      showAlert('Failed to load resource templates.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setIsEditing(true);
    } else {
      setCurrentItem({
        category: '',
        exampleName: '',
        exampleDetails: '',
        order: items.length + 1,
        publishedAt: null
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value
    });
  };

  const handleSubmit = async (publish = false) => {
    try {
      setLoading(true);
      const dataToSubmit = {
        ...currentItem,
        publishedAt: publish ? new Date().toISOString() : currentItem.publishedAt
      };

      if (isEditing) {
        await idpConfigurationApi.updateResourceTemplate(currentItem.id, dataToSubmit);
        showAlert('Resource template updated successfully', 'success');
      } else {
        await idpConfigurationApi.createResourceTemplate(dataToSubmit);
        showAlert('Resource template created successfully', 'success');
      }
      handleCloseDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} resource template.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await idpConfigurationApi.deleteResourceTemplate(currentItem.id);
      showAlert('Resource template deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || 'Failed to delete resource template.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishClick = (item) => {
    setPublishModal({ open: true, item });
  };

  const confirmPublish = async () => {
    try {
      setLoading(true);
      await idpConfigurationApi.updateResourceTemplate(publishModal.item.id, {
        ...publishModal.item,
        publishedAt: new Date().toISOString()
      });
      showAlert('Resource template published successfully', 'success');
      await fetchItems();
      setPublishModal({ open: false, item: null });
    } catch (err) {
      showAlert(err.message || 'Failed to publish resource template.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublishClick = (item) => {
    setUnpublishModal({ open: true, item });
  };

  const confirmUnpublish = async () => {
    try {
      setLoading(true);
      await idpConfigurationApi.updateResourceTemplate(unpublishModal.item.id, {
        ...unpublishModal.item,
        publishedAt: null
      });
      showAlert('Resource template unpublished successfully', 'success');
      await fetchItems();
      setUnpublishModal({ open: false, item: null });
    } catch (err) {
      showAlert(err.message || 'Failed to unpublish resource template.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.category.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    item.exampleName?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/idp-configuration')}>
            <FaArrowLeft /> Back to IDP Configuration
          </BackButton>
          <Title>Resource Templates Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Resource Templates"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Template
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredItems.length}
            </span>
            {filteredItems.length === 1 ? 'template' : 'templates'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No resource templates found"
              description={searchTerm ?
                "No resource templates match your search criteria." :
                "No resource templates have been added yet. Click the '+ Add Template' button to add one."}
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <ItemTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.category}
                        {item.publishedAt ? (
                          <Chip size="small" label="Published" color="success" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Draft" color="default" variant="outlined" />
                        )}
                      </Box>
                      <OrderBadge>
                        Order: {item.order}
                      </OrderBadge>
                    </ItemTitle>
                    <DetailRow>
                      <Label>Example:</Label> {item.exampleName || <ItalicText>Not provided</ItalicText>}
                    </DetailRow>
                    <ItemDetailsGrid>
                      {item.exampleDetails ? item.exampleDetails : <ItalicText>No details provided</ItalicText>}
                    </ItemDetailsGrid>
                    <DetailRow style={{ marginTop: '8px', fontSize: '12px', color: '#64748B' }}>
                      <Label>Published:</Label> {item.publishedAt ? formatDateOnly(item.publishedAt) : <ItalicText>Not published</ItalicText>}
                    </DetailRow>
                    <ActionButtons>
                      {!item.publishedAt ? (
                        <IconButton
                          size="small"
                          onClick={() => handlePublishClick(item)}
                          color="success"
                          title="Publish Template"
                        >
                          <CloudUploadIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleUnpublishClick(item)}
                          color="warning"
                          title="Unpublish Template"
                        >
                          <CloudOffIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        title="Edit Template"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(item)}
                        color="error"
                        title="Delete Template"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Resource Template' : 'Add Resource Template'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="category"
            label="Category (e.g., Training & Education, Mentorship)"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.category}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="exampleName"
            label="Example Name (e.g., Online Course)"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.exampleName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="exampleDetails"
            label="Example Details"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={currentItem.exampleDetails}
            onChange={handleInputChange}
            helperText="E.g., Coursera, Udemy, LinkedIn Learning"
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentItem.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => handleSubmit(false)}
            variant="contained"
            color="primary"
            disabled={!currentItem.category.trim()}
          >
            {isEditing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the resource template "{currentItem.category}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        isOpen={publishModal.open}
        onClose={() => setPublishModal({ open: false, item: null })}
        onConfirm={confirmPublish}
        title="Publish Resource Template"
        message={`Are you sure you want to publish the resource template "${publishModal.item?.category}"? Once published, it will be available for use in development plans.`}
        actionButtonText="Publish"
      />

      {/* Unpublish Confirmation Modal */}
      <ConfirmationModal
        isOpen={unpublishModal.open}
        onClose={() => setUnpublishModal({ open: false, item: null })}
        onConfirm={confirmUnpublish}
        title="Unpublish Resource Template"
        message={`Are you sure you want to unpublish the resource template "${unpublishModal.item?.category}"? It will no longer be available for new development plans.`}
        actionButtonText="Unpublish"
      />
    </Container>
  );
};

export default ResourceTemplates;
